import React, { useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import { suggestAlphabeticCode, suggestNumericCode } from "./suggestInternalCode";
import { catalogRequestError, internalCodeText } from "./internalCatalogCodes";
import "./InternalCodeSuggestions.css";

const SPANISH = {
  "Suggest numeric": "Sugerir numérico", "Suggest alphanumeric": "Sugerir alfanumérico",
  "Checking available codes...": "Buscando códigos disponibles…",
  "The catalog did not return its list of codes. Reload the form or enter a code manually.": "El catálogo no ha devuelto la lista de códigos. Recarga el formulario o introduce un código manualmente.",
  "The returned codes belong to another business unit. Reload the form in the intended business unit.": "Los códigos recibidos pertenecen a otra unidad de negocio. Recarga el formulario en la unidad que corresponda.",
  "The catalog did not respond to the code lookup. Check the connection or enter a code manually.": "No se han podido comprobar los códigos porque el catálogo no ha respondido. Comprueba la conexión o introduce un código manualmente.",
  "No unused six-letter combination was found. Try a numeric code or enter one manually.": "No hay una combinación libre de seis letras. Prueba un código numérico o introdúcelo a mano.",
  "Enter the name first to suggest a code.": "Introduce primero el nombre para sugerir un código.",
  "Six letters from the name; short names repeat letters.": "Seis letras del nombre; en nombres cortos se repiten letras.",
};

const entityTypes = { items: "item", families: "family", subfamilies: "subfamily", categories: "category", subcategories: "subcategory" };

export default function InternalCodeSuggestions({ name, value, resource, onSuggest, disabled = false, languageId: languageOverride, requestClient = axios, excludeRequestId }) {
  const { languageId } = usePmsTheme();
  const language = languageOverride || languageId;
  const t = (text) => (language === "es" && SPANISH[text]) || internalCodeText(language, text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef({ id: 0, controller: null });
  const scope = JSON.stringify([readStoredUser()?.active_business_unit_id, resource, name, value, disabled, excludeRequestId]);
  const latest = useRef({ scope, onSuggest });
  latest.current = { scope, onSuggest };
  useEffect(() => {
    request.current.controller?.abort(); request.current.id += 1;
    setBusy(false); setError("");
    return () => { request.current.controller?.abort(); request.current.id += 1; };
  }, [scope]);

  const suggest = async (mode) => {
    const businessUnitId = readStoredUser()?.active_business_unit_id;
    if (disabled || busy || !String(name || "").trim() || !businessUnitId) return;
    const requestId = request.current.id + 1;
    const active = { id: requestId, controller: new AbortController() };
    request.current = active; setBusy(true); setError("");
    const isCurrent = () => request.current.id === requestId && latest.current.scope === scope
      && String(readStoredUser()?.active_business_unit_id) === String(businessUnitId);
    try {
      const query = new URLSearchParams({ entity_type: entityTypes[resource], business_unit_id: businessUnitId });
      if (excludeRequestId != null) query.set("request_id", excludeRequestId);
      const response = await requestClient.get(`/api/purchasing/catalog-creation-approvals/codes?${query}`, { signal: active.controller.signal });
      if (!isCurrent()) return;
      if (!Array.isArray(response.data?.codes)) { setError("The catalog did not return its list of codes. Reload the form or enter a code manually."); return; }
      if (Number(response.data.business_unit_id) !== Number(businessUnitId)) { setError("The returned codes belong to another business unit. Reload the form in the intended business unit."); return; }
      const code = mode === "numeric" ? suggestNumericCode(response.data.codes) : suggestAlphabeticCode(name, response.data.codes);
      if (!code) setError("No unused six-letter combination was found. Try a numeric code or enter one manually.");
      else latest.current.onSuggest(code);
    } catch (requestError) {
      if (isCurrent()) setError(catalogRequestError(requestError, requestError.response
        ? `The catalog could not return the list of codes (HTTP ${requestError.response.status}).`
        : "The catalog did not respond to the code lookup. Check the connection or enter a code manually."));
    } finally {
      if (request.current.id === requestId) setBusy(false);
    }
  };

  const unavailable = disabled || busy || !String(name || "").trim() || !readStoredUser()?.active_business_unit_id;
  return <div className="ics-codeSuggestions">
    <div className="ics-codeActions">
      <button type="button" disabled={unavailable} onClick={() => suggest("numeric")}>{t("Suggest numeric")}</button>
      <button type="button" disabled={unavailable} onClick={() => suggest("alphabetic")} title={t("Six letters from the name; short names repeat letters.")}>{t("Suggest alphanumeric")}</button>
    </div>
    {!String(name || "").trim() && <small>{t("Enter the name first to suggest a code.")}</small>}
    {busy && <small role="status">{t("Checking available codes...")}</small>}
    {error && <small role="alert" className="ics-codeError">{t(error)}</small>}
  </div>;
}
